import React, { useState, useEffect } from 'react';

const POLL_INTERVAL = 2000; // 2 seconds
const MAX_ATTEMPTS = 10; // 20 seconds total

const Dialog = ({ title, description, buttonText, onButtonClick }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80">
    <div className="bg-slate-800 p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
      <h2 className="text-xl font-semibold text-slate-100 mb-2">
        {title}
      </h2>
      <p className="text-slate-300 mb-6">
        {description}
      </p>
      <div className="flex justify-end">
        <button
          onClick={onButtonClick}
          className="px-4 py-2 bg-emerald-500 text-slate-900 rounded-md hover:bg-emerald-400"
        >
          {buttonText}
        </button>
      </div>
    </div>
  </div>
);

const PaymentSuccessHandler = ({ onSuccess }) => {
  const [status, setStatus] = useState('checking');
  // Status can be: 'checking', 'success', 'error'

  useEffect(() => {
    let attempts = 0;
    let timeoutId;

    const checkStatus = async () => {
      try {
        const token = localStorage.getItem('sessionToken');
        if (!token) {
          throw new Error('No session token found');
        }

        const response = await fetch(`${import.meta.env.VITE_WORKER_URL}/subscription-status`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw new Error('auth_error');
          }
          throw new Error('api_error');
        }

        const data = await response.json();
        
        if (data.tier !== 'free') {
          setStatus('success');
          await onSuccess();
          window.history.replaceState({}, '', '/');
          return true;
        }

        return false;
      } catch (error) {
        if (error.message === 'auth_error') {
          setStatus('auth_error');
          return true;
        }
        return false;
      }
    };

    const poll = async () => {
      const success = await checkStatus();
      
      if (success) {
        return; // Stop polling on success or auth error
      }

      attempts++;
      if (attempts >= MAX_ATTEMPTS) {
        setStatus('timeout');
      } else {
        timeoutId = setTimeout(poll, POLL_INTERVAL);
      }
    };

    poll();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [onSuccess]);

  if (status === 'checking') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/80">
        <div className="bg-slate-800 p-6 rounded-lg shadow-xl text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-slate-100 mb-2">
            Activating Your Subscription
          </h2>
          <p className="text-slate-300">
            Please wait while we activate your subscription...
          </p>
        </div>
      </div>
    );
  }

  if (status === 'auth_error') {
    return (
      <Dialog
        title="Session Expired"
        description="Your session has expired. Please log in again to view your subscription status."
        buttonText="Log In Again"
        onButtonClick={() => window.location.reload()}
      />
    );
  }

  if (status === 'timeout') {
    return (
      <Dialog
        title="Subscription Status Pending"
        description="Your subscription is being processed. This may take a few minutes. You can continue using the app, and your new features will be available shortly."
        buttonText="Refresh Now"
        onButtonClick={() => window.location.reload()}
      />
    );
  }

  return null;
};

export default PaymentSuccessHandler;
